import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { CardHeader, CardSection, CardWrapper } from 'foundations-components/transitional/components/Card';
import { Flex } from 'foundations-components/components/Flex';
import { Link } from 'foundations-components/transitional/components/Link';
import { Text } from 'foundations-components/components/Text';
import { captureNetworkError } from 'growth-onboarding-reliability/utils/raven';
import * as manualDetectedTasks from 'getting-started-shared-tasks/manualDetectedTasks';
import HelpWidgetButton from '../button/HelpWidgetButton';
import TaskActionButton from '../button/TaskActionButton';
import TaskDuration from './TaskDuration';
import { TASK_FADE_DURATION_MS } from '../../../constants/task';
import { useVisibilityChange } from '../../../hooks/element';
import { useTaskApi, useUncompletedDependencyTasks } from '../../../hooks/task';
import { useTracker } from '../../../hooks/tracking';
import { fadeIn, fadeOut } from '../../../utils/keyFrames';
import { TaskAssignmentBadge } from '../../modal/TaskAssignmentBadge';
import { FEATURE_SWITCH_APPS, FeatureSwitch } from '../../FeatureSwitch';
import TaskAssignmentLinks from './TaskAssignmentLinks';
import { ButtonWrapper } from 'foundations-components/transitional/components/ButtonWrapper';
import { Spacer } from 'foundations-components/components/Spacer';

/* @bend-expect no-styled-foundations-components */
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const UncompletedTaskCard = styled(CardWrapper).withConfig({
  displayName: "UncompletedTask__UncompletedTaskCard",
  componentId: "nabldz-0"
})(["animation:", " ", "ms ease-in-out;"], ({
  isTaskActioned
}) => isTaskActioned ? fadeOut : fadeIn, TASK_FADE_DURATION_MS);
const UncompletedTask = ({
  expanded,
  durationInMinutes,
  onExpandedChange,
  task
}) => {
  const {
    checkAndCompleteTask,
    completeTask,
    skipTask
  } = useTaskApi();
  const [isTaskActioned, setIsTaskActioned] = useState(false);
  const {
    trackTaskInteraction
  } = useTracker();
  const {
    key
  } = task;
  useEffect(() => {
    if (Object.values(manualDetectedTasks).includes(key)) {
      checkAndCompleteTask(key).catch(captureNetworkError);
    }
  }, [checkAndCompleteTask, key]);
  const fulfillTaskAction = useCallback(taskAction => {
    setIsTaskActioned(true); // fade out current task
    window.setTimeout(() => taskAction(key), TASK_FADE_DURATION_MS);
  }, [key]);
  const onVisibilityChange = useCallback(isVisible => {
    if (isVisible && expanded) {
      checkAndCompleteTask(key, fulfillTaskAction).catch(captureNetworkError);
    }
  }, [checkAndCompleteTask, expanded, fulfillTaskAction, key]);
  useVisibilityChange(onVisibilityChange);
  const dependencyTasks = useUncompletedDependencyTasks(task.dependentTasks);
  return (
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsxs(UncompletedTaskCard, {
      compact: true,
      "data-onboarding": `task:${task.key}`,
      "data-task-status": "uncompleted",
      "data-test-id": `task:${key}`,
      expandable: true,
      expanded: !isTaskActioned && expanded,
      isTaskActioned: isTaskActioned,
      onExpandedChange: isExpanded => onExpandedChange(isExpanded, 'uncompleted'),
      children: [/*#__PURE__*/_jsx(CardHeader, {
        "data-test-id": "task-header",
        paddingInline: "400",
        title: /*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          gap: "1500",
          justifyContent: "space-between",
          children: [/*#__PURE__*/_jsxs(Flex, {
            alignItems: "stretch",
            flexDirection: "column",
            gap: "100",
            children: [/*#__PURE__*/_jsx("span", {
              children: task.name
            }), /*#__PURE__*/_jsx(TaskDuration, {
              duration: durationInMinutes
            })]
          }), /*#__PURE__*/_jsx(FeatureSwitch, {
            allowedApps: [FEATURE_SWITCH_APPS.USER_GUIDE],
            children: /*#__PURE__*/_jsx(TaskAssignmentBadge, {
              taskId: task.id
            })
          })]
        }),
        titleSize: "small"
      }), /*#__PURE__*/_jsx(CardSection, {
        "data-test-id": "task-section",
        paddingBlockEnd: "0",
        children: /*#__PURE__*/_jsxs(Flex, {
          alignItems: "stretch",
          flexDirection: "column",
          gap: "400",
          paddingBlockEnd: "500",
          paddingInline: "400",
          children: [/*#__PURE__*/_jsxs(Text, {
            as: "span",
            children: [task.description && /*#__PURE__*/_jsxs(_Fragment, {
              children: [typeof task.description === 'string' ? task.description.trim() : task.description, /*#__PURE__*/_jsx(Spacer, {
                display: "inline-block",
                space: "100"
              })]
            }), /*#__PURE__*/_jsx(HelpWidgetButton, {
              task: task
            })]
          }), /*#__PURE__*/_jsxs(ButtonWrapper, {
            flexWrap: "nowrap",
            maxBlockSize: "100%",
            overflow: "hidden",
            children: [/*#__PURE__*/_jsx(TaskActionButton, {
              checkAndCompleteTask: checkAndCompleteTask,
              completeTask: completeTask,
              task: task
            }), !dependencyTasks && /*#__PURE__*/_jsx(FeatureSwitch, {
              allowedApps: [FEATURE_SWITCH_APPS.USER_GUIDE],
              skipFeatureLoad: !expanded,
              children: /*#__PURE__*/_jsx(TaskAssignmentLinks, {
                task: task
              })
            })]
          }), task.skippable !== false && /*#__PURE__*/_jsx(Link, {
            "data-onboarding": "skip-task",
            "data-test-id": "skip-task",
            onClick: () => {
              trackTaskInteraction('click task skip', task.key);
              fulfillTaskAction(skipTask);
            },
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "setup-guide-components.checklist.skip"
            })
          })]
        })
      })]
    })
  );
};
export default UncompletedTask;